import { useMemo } from "react";
import { CalendarDays, ExternalLink, MapPin } from "lucide-react";
import { CalendarLinks } from "@/components/CalendarLinks";
import { motion } from "framer-motion";

import { Button } from "@/components/ui/button";
import { formatDate, formatDateRange, shortDate } from "@/utils/format";

function weekRange() {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

export function WeeklyShoots({ calendar = [] }) {
  const { start, end } = useMemo(() => weekRange(), []);
  const weekly = calendar.filter((item) => {
    const first = new Date(`${item.date}T12:00:00`);
    const last = new Date(`${item.end_date || item.date}T12:00:00`);
    return first <= end && last >= start;
  });
  const range = `${new Intl.DateTimeFormat("es-ES", { day: "numeric", month: "short" }).format(start)} — ${new Intl.DateTimeFormat("es-ES", { day: "numeric", month: "short" }).format(end)}`;

  return (
    <section id="semana" className="section-shell section-shell--tight" data-testid="weekly-shoots-section">
      <div className="mb-10 flex flex-col justify-between gap-6 border-b border-white/10 pb-8 lg:flex-row lg:items-end">
        <div>
          <p className="eyebrow" data-testid="weekly-shoots-eyebrow">Agenda inmediata</p>
          <h2 className="mt-4 font-display text-6xl uppercase leading-[0.86] text-slate-50 sm:text-7xl">Tiradas de esta semana</h2>
          <p className="mt-4 max-w-2xl text-base leading-8 text-slate-400">Todos los eventos publicados en la hoja oficial para la semana en curso, agrupados por fecha, modalidad y campo.</p>
        </div>
        <div className="border border-clay/35 bg-clay/10 px-5 py-4 text-right" data-testid="weekly-range-card">
          <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-slate-400">Semana</span>
          <span className="font-display text-4xl uppercase leading-none text-clay">{range}</span>
          <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-slate-400">{weekly.length} eventos</span>
        </div>
      </div>

      {weekly.length ? (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="weekly-shoots-list">
          {weekly.map((item, index) => (
            <motion.article key={item.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }} className="weekly-card" data-testid={`weekly-event-${item.id}`}>
              <div className="weekly-date">
                <span className="font-display text-5xl leading-none text-clay">{shortDate(item.date).split(" ")[0]}{item.end_date ? <span className="text-3xl text-slate-400">–{shortDate(item.end_date).split(" ")[0]}</span> : null}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">{shortDate(item.date).split(" ")[1]}</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay">{item.modality}</span>
                  {item.targets ? <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">· {item.targets} platos</span> : null}
                </div>
                <h3 className="mt-2 font-display text-3xl uppercase leading-none text-slate-50">{item.title}</h3>
                <p className="mt-3 flex items-center gap-2 text-sm text-slate-400"><MapPin className="h-4 w-4 shrink-0 text-clay" />{item.club ? `${item.club} · ` : ""}{item.location} · {item.province}</p>
              </div>
              <div className="flex flex-wrap gap-2 self-start sm:justify-end sm:max-w-xs">
                {item.poster_url ? (
                  <Button asChild variant="outline" className="rounded-none border-clay/40 bg-clay/10 font-mono text-[10px] uppercase tracking-[0.2em] text-clay hover:bg-clay hover:text-charcoal">
                    <a href={item.poster_url} target="_blank" rel="noreferrer" data-testid={`weekly-poster-${item.id}`}>Cartel <ExternalLink className="h-3 w-3" /></a>
                  </Button>
                ) : null}
                <CalendarLinks item={item} prefix={`weekly-results-${item.id}`} />
              </div>
            </motion.article>
          ))}
        </div>
      ) : (
        <div className="border border-dashed border-white/15 p-8 text-slate-400" data-testid="weekly-empty-state">
          <CalendarDays className="mb-4 h-6 w-6 text-clay" />
          No hay tiradas publicadas para esta semana en la hoja oficial.
        </div>
      )}
    </section>
  );
}
